import { useMemo } from 'react';
import { Form, Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { usePriceGroupTab } from 'core/contexts/priceGroupTab';
import { PriceStatus } from '@utriper/core';
import { Box, Button, CircularProgress, Grid2, InputLabel, Typography } from '@mui/material';
import { CharacterCounter, SelectComponent, TextInput } from '@utriper/core-ui';

const PriceGroupTabForm = ({ priceGroupTabId = 0, formInitialValues = { name: '', description: '', status: PriceStatus.IDS.live } }) => {
    const { t, i18n } = useTranslation();
    const translate = (value) => t(`price_group_tab.form.${value}`);
    const { createPriceGroupTab, updatePriceGroupTab, handleOpenForm, handleOpenUpdateForm, submitting } = usePriceGroupTab();
    const statusOptions = useMemo(
        () =>
            PriceStatus.getIds().map((status) => ({
                id: status,
                name: PriceStatus.getTranslatedName(PriceStatus.status, status, i18n.language),
            })),
        [i18n.language]
    );

    const isUpdate = Number.isInteger(priceGroupTabId) && priceGroupTabId > 0 && formInitialValues?.name?.length > 0;

    const handleSubmit = (values) => {
        if (isUpdate) {
            updatePriceGroupTab(priceGroupTabId, values);
            return;
        }

        createPriceGroupTab(values);
    };

    const handleOpen = () => {
        if (isUpdate) {
            handleOpenUpdateForm(false);
            return;
        }

        handleOpenForm(false);
    };

    const validateForm = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = translate('errors.required');
        }
        if (values?.name?.length > 255) {
            errors.name = translate('errors.length');
        }
        if (values?.description && values?.description?.length > 255) {
            errors.description = translate('errors.length');
        }

        return errors;
    };

    return (
        <Form
            key={`price-group-tab-form-${priceGroupTabId}`}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validate={validateForm}
            render={({ values, handleSubmit, form }) => (
                <form key={`price-group-tab-form-${priceGroupTabId}`} onSubmit={handleSubmit} noValidate>
                    <Box component="section" py={2}>
                        {/* Title */}
                        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                            <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                {isUpdate ? translate('update.title') : translate('add.title')}
                            </Typography>
                            <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                                {translate('add.description')}
                            </Typography>
                        </Grid2>

                        {/* Form */}
                        <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                            {/* Name */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <InputLabel
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 400,
                                        color: '#131313',
                                    }}
                                >
                                    {translate('label.name')}
                                </InputLabel>
                                <Field
                                    name="name"
                                    component={TextInput}
                                    placeholder={translate('placeholder.name')}
                                    slotProps={{
                                        input: {
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.name?.length ?? 0} />
                            </Grid2>
                            {/* Description */}
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <InputLabel
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 400,
                                        color: '#131313',
                                    }}
                                >
                                    {translate('label.description')}
                                </InputLabel>
                                <Field
                                    name="description"
                                    component={TextInput}
                                    placeholder={translate('placeholder.description')}
                                    multiline
                                    minRows={2}
                                    slotProps={{
                                        input: {
                                            sx: {
                                                '& textarea': {
                                                    resize: 'vertical',
                                                    minHeight: '32px',
                                                    maxHeight: '160px',
                                                    overflow: 'auto',
                                                },
                                            },
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        },
                                    }}
                                />
                                <CharacterCounter length={values?.description?.length ?? 0} />
                            </Grid2>
                            <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="4px">
                                <InputLabel
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 400,
                                        color: '#131313',
                                    }}
                                >
                                    {translate('label.status')}
                                </InputLabel>
                                <Field name="status" component={SelectComponent} values={statusOptions} />
                            </Grid2>
                        </Grid2>

                        {/* Action Buttons */}
                        <Grid2 container justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center" gap="12px" mt="16px">
                            <Button
                                sx={{
                                    padding: '8px 14px',
                                    borderColor: '#13131340',
                                    fontWeight: 700,
                                    width: { xs: '100%', md: 'fit-content' },
                                    textTransform: 'none',
                                }}
                                variant="outlined"
                                onClick={(e) => {
                                    handleOpen();
                                    return form.reset(e);
                                }}
                            >
                                {translate('buttons.cancel')}
                            </Button>
                            <Button
                                sx={{
                                    padding: '8px 14px',
                                    backgroundColor: 'var(--color-utriper-blue)',
                                    fontWeight: 700,
                                    width: { xs: '100%', md: 'fit-content' },
                                    textTransform: 'none',
                                }}
                                loading={submitting}
                                loadingIndicator={
                                    <Box sx={{ display: 'flex' }}>
                                        <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                    </Box>
                                }
                                variant="contained"
                                type="submit"
                                onClick={(e) => {
                                    return handleSubmit(e);
                                }}
                            >
                                {isUpdate ? translate('buttons.update') : translate('buttons.add')}
                            </Button>
                        </Grid2>
                    </Box>
                </form>
            )}
        />
    );
};

export default PriceGroupTabForm;
